import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { type ChatMessage } from "@/hooks/useChatHistory";
import { supabase } from "@/integrations/supabase/client";
import { messageAttachmentSchema } from "@repo/supabase/schemas";
import { useQuery } from "@tanstack/react-query";
import { isToday, isYesterday } from "date-fns";
import { forwardRef } from "react";
import { z } from "zod";

export interface MessageBubbleProps {
  message: ChatMessage;
  currentCharacterName: string;
  isLastMessage: boolean;
  userId: string;
  characterId: string;
}

// Returns formatted time and day info for a message
const getMessageTimeInfo = (createdAt: string): string => {
  const msgDate = new Date(createdAt);
  let dayInfo = "";
  if (!isToday(msgDate)) {
    if (isYesterday(msgDate)) {
      dayInfo = "hier";
    } else {
      dayInfo = msgDate.toLocaleDateString("fr-FR", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      });
    }
  }
  const time = msgDate.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return dayInfo ? `${dayInfo} à ${time}` : time;
};

export const MessageBubble = forwardRef<HTMLDivElement, MessageBubbleProps>(
  ({ message, isLastMessage, userId, characterId }, ref) => {
    const isUser = message.sender === "user";
    const status = message.status;

    // Remove custom modal state

    // Parse attachments using zod
    const parsedAttachments = z
      .array(messageAttachmentSchema)
      .safeParse(message.attachments);

    const imageAttachment =
      parsedAttachments.success ?
        parsedAttachments.data.find((attachment) => attachment.type === "image")
      : null;

    // Use react-query to fetch signed URL if imageAttachment exists
    const { data: imageUrl } = useQuery({
      queryKey: [
        "message-image-url",
        userId,
        characterId,
        imageAttachment?.file_name,
      ],
      queryFn: async () => {
        if (!imageAttachment) return null;
        const { data } = await supabase.storage
          .from("conversation_images")
          .createSignedUrl(
            `${userId}/${characterId}/${imageAttachment.file_name}`,
            3600,
          );
        return data?.signedUrl ?? null;
      },
      enabled: !!imageAttachment,
      staleTime: 3600 * 1000,
    });

    return (
      <>
        <div
          ref={ref}
          className={`flex ${isUser ? "justify-end" : "justify-start"} animate-fade-in py-1.5`}
        >
          <div className={`relative flex max-w-xs flex-col sm:max-w-sm`}>
            <div
              className={`shadow-xs relative rounded-2xl px-4 py-2 text-sm ${
                isUser ?
                  "rounded-tr-xs bg-gradient-to-br from-indigo-500 to-blue-600 text-white"
                : "rounded-tl-xs border border-gray-200 bg-white/90 text-gray-900"
              }`}
            >
              <p className="break-words leading-relaxed">{message.message}</p>
              {imageUrl ?
                <Dialog>
                  <DialogTrigger asChild>
                    <img
                      src={imageUrl}
                      alt="Attachment"
                      className="mt-2 max-h-48 cursor-pointer rounded-lg object-contain"
                      style={{ maxWidth: "100%" }}
                    />
                  </DialogTrigger>
                  <DialogContent
                    closeButtonClassName="text-white"
                    className="flex h-full max-h-none w-full max-w-full items-center justify-center rounded-none border-0 bg-black p-0"
                  >
                    <img
                      src={imageUrl}
                      alt="Attachment fullscreen"
                      className="max-h-full max-w-full object-contain"
                    />
                  </DialogContent>
                </Dialog>
              : null}
              <div className="mt-1 flex items-center justify-end gap-1">
                <span
                  className={`text-xs ${isUser ? "text-indigo-100" : "text-gray-500"}`}
                >
                  {getMessageTimeInfo(message.created_at)}
                </span>
              </div>
            </div>
            {isUser && isLastMessage && (
              <span className="text-muted-foreground ml-auto mr-3 mt-1 text-sm">
                {status === "read" ?
                  "Lu"
                : status === "delivered" ?
                  "Reçu"
                : "Envoyé"}
              </span>
            )}
          </div>
        </div>
      </>
    );
  },
);
MessageBubble.displayName = "MessageBubble";
